// Pure, framework-free upload-validation helpers shared by every UploadMedia
// entry point (file picker + dropzone drag-drop). Extracted so the type/size
// guard is a single source of truth and can be unit-tested directly.
//
// The caller passes the ALREADY-RESOLVED list of accepted extensions
// (`effectiveFormats` — which the component derives per-surface, e.g. the
// wallpaper picker strips "gif"), so this module owns no type map of its own.

export const MAX_FILE_SIZE_MB = 50;

export const getFileExtension = (name) =>
  String(name || "")
    .split(".")
    .pop()
    .toLowerCase();

export const isExtensionAllowed = (file, allowedExtensions) =>
  (allowedExtensions || []).includes(getFileExtension(file.name));

export const isFileSizeAllowed = (file, maxMB = MAX_FILE_SIZE_MB) =>
  file.size / 1000000 <= maxMB;

/**
 * Split an incoming file list into the files that pass BOTH the type and size
 * guard, plus a flag for whether anything was rejected on either axis.
 * @param {FileList|File[]} files
 * @param {string[]} allowedExtensions - resolved accepted extensions for the surface
 * @param {number} maxMB - size cap in MB
 * @returns {{ valid: File[], rejectedType: boolean, rejectedSize: boolean }}
 */
export const partitionUploadFiles = (
  files,
  allowedExtensions,
  maxMB = MAX_FILE_SIZE_MB
) => {
  const list = Array.from(files || []);
  const typeValid = list.filter((file) =>
    isExtensionAllowed(file, allowedExtensions)
  );
  const rejectedType = typeValid.length !== list.length;
  const valid = typeValid.filter((file) => isFileSizeAllowed(file, maxMB));
  const rejectedSize = valid.length !== typeValid.length;
  return { valid, rejectedType, rejectedSize };
};
